//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { BodyText, TitleText } from '@components/atoms/text/Text';
import RightsImage from '@images/na_rights.svg';
import { SocialNetwork } from '@models/SocialNetwork';
import { getIsTikTokMentionedRightsEnabled } from '@uia/rights/selectors/getUIARights';
import { isDefined } from '@utils/filterPredicates';

export interface UIANotSupportedRightsProps {
  socialNetwork: SocialNetwork;
  isRightsRequestsTwitterEnabled?: boolean;
  originalUrl?: string;
  userName?: string;
}

export const UIANotSupportedRights = ({
  isRightsRequestsTwitterEnabled,
  originalUrl,
  socialNetwork,
  userName,
}: UIANotSupportedRightsProps) => {
  const isTikTokMentionedRightsEnabled = useSelector(
    getIsTikTokMentionedRightsEnabled,
  );
  const getNotSupportedMessage = () => {
    if (socialNetwork === SocialNetwork.Instagram && !isDefined(originalUrl)) {
      return (
        <>
          We can't find the link to this <br />
          Instagram media.
        </>
      );
    }

    if (socialNetwork === SocialNetwork.TikTok && !isDefined(userName)) {
      return (
        <>
          We can't send a rights request because <br />
          the creator's username is missing for this video.
        </>
      );
    }

    return (
      <>
        You can only request rights on an <br />
        Instagram {isTikTokMentionedRightsEnabled && <span>, or TikTok</span>}
        {isRightsRequestsTwitterEnabled && <span>, or Twitter</span>} image or
        video.
      </>
    );
  };

  return (
    <Wrapper>
      <Image alt='Rights icon' src={RightsImage} />
      <TitleText>Not Supported</TitleText>
      <BodyText>{getNotSupportedMessage()}</BodyText>
    </Wrapper>
  );
};

const Wrapper = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacings[7]} ${theme.spacings[0]};
    width: 100%;
    text-align: center;
  `,
);

const Image = styled.img(
  ({ theme }) => `
    height: auto;
    width: calc(3 * ${theme.spacings[6]});
  `,
);
